import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Form,
  Grid,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Progress,
  Skeleton,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import ReusableUpload from "../../components/upload/ReusableUpload";
import {
  getSelectedOrganizationId,
  getSelectedOrganizationName,
  hasWorkingOrganization,
} from "../../api/Helper";
import {
  deleteFaissIndex,
  downloadFaissIndex,
  FaissIndexFile,
  getProductSimilaritySettings,
  listFaissIndexes,
  ProductSimilaritySettings,
  setActiveFaissIndex,
  updateProductSimilaritySettings,
  uploadFaissIndex,
} from "../../api/Services";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";
import { getTablePagination } from "../../helpers/TablePaginationHelper";
import styles from "./FaissIndexes.module.scss";

const { Title, Text } = Typography;

const DEFAULT_SETTINGS: Omit<ProductSimilaritySettings, "organizationId"> = {
  topK: 100,
  skipDuplicates: true,
  maxDistance: 20,
  sameCategory: false,
  sameBrand: true,
  maxPriceDifferencePerc: 25,
};

type UploadProgressState = {
  filename: string;
  loaded: number;
  total: number;
  percent: number;
  startedAt: number;
};

type FaissIndexesProps = {
  /** When true, skip AdminValidation and page title (used inside Análisis tabs). */
  embedded?: boolean;
};

const formatBytes = (size?: number) => {
  if (size == null || Number.isNaN(size)) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024)
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatEta = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.max(0, Math.round(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
};

const FaissIndexes = ({ embedded = false }: FaissIndexesProps) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobileList = !screens.md;

  const workingOrganizationId =
    hasWorkingOrganization() && getSelectedOrganizationId()
      ? Number(getSelectedOrganizationId())
      : undefined;
  const workingOrganizationName = hasWorkingOrganization()
    ? getSelectedOrganizationName()
    : undefined;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressState | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [files, setFiles] = useState<FaissIndexFile[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [page, setPage] = useState(1);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm] = Form.useForm<Omit<ProductSimilaritySettings, "organizationId">>();

  const fetchIndexes = useCallback(async () => {
    if (!workingOrganizationId) {
      setFiles([]);
      setTotalSize(0);
      return;
    }
    setLoading(true);
    try {
      const result = await listFaissIndexes(workingOrganizationId, page - 1, 10);
      setFiles(result.items);
      setTotalSize(result.totalElements);
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    } finally {
      setLoading(false);
    }
  }, [workingOrganizationId, page, t]);

  const fetchSettings = useCallback(async () => {
    if (!workingOrganizationId) {
      settingsForm.setFieldsValue(DEFAULT_SETTINGS);
      return;
    }
    setSettingsLoading(true);
    try {
      const settings = await getProductSimilaritySettings(workingOrganizationId);
      settingsForm.setFieldsValue({
        topK: settings.topK,
        skipDuplicates: settings.skipDuplicates,
        maxDistance: settings.maxDistance,
        sameCategory: settings.sameCategory,
        sameBrand: settings.sameBrand,
        maxPriceDifferencePerc: settings.maxPriceDifferencePerc,
      });
    } catch (e) {
      settingsForm.setFieldsValue(DEFAULT_SETTINGS);
      message.error(getApiErrorMessage(e, t("FaissIndexes-SettingsLoadError")));
    } finally {
      setSettingsLoading(false);
    }
  }, [workingOrganizationId, settingsForm, t]);

  useEffect(() => {
    setPage(1);
    setFileList([]);
  }, [workingOrganizationId]);

  useEffect(() => {
    fetchIndexes();
  }, [fetchIndexes]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    if (!workingOrganizationId) return;
    try {
      const values = await settingsForm.validateFields();
      setSettingsSaving(true);
      await updateProductSimilaritySettings({
        organizationId: workingOrganizationId,
        ...values,
      });
      message.success(t("FaissIndexes-SettingsSaveSuccess"));
    } catch (e) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      message.error(getApiErrorMessage(e, t("FaissIndexes-SettingsSaveError")));
    } finally {
      setSettingsSaving(false);
    }
  };

  useEffect(() => {
    if (!uploading) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [uploading]);

  const etaLabel = useMemo(() => {
    if (!uploadProgress) return null;
    const { loaded, total, percent, startedAt } = uploadProgress;
    if (percent >= 100) return t("FaissIndexes-UploadProcessing");
    if (!total || loaded <= 0) return t("FaissIndexes-UploadEtaCalculating");
    const elapsedSec = (Date.now() - startedAt) / 1000;
    if (elapsedSec < 1) return t("FaissIndexes-UploadEtaCalculating");
    const bytesPerSec = loaded / elapsedSec;
    if (bytesPerSec <= 0) return t("FaissIndexes-UploadEtaCalculating");
    const remainingSec = (total - loaded) / bytesPerSec;
    const eta = formatEta(remainingSec);
    if (!eta) return t("FaissIndexes-UploadEtaCalculating");
    return t("FaissIndexes-UploadEta", { eta });
  }, [uploadProgress, t]);

  const handleUploadRequest = async (options: {
    file: File;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  }) => {
    if (!workingOrganizationId) {
      message.warning(t("FaissIndexes-SelectOrganizationFirst"));
      options.onError(new Error("organization required"));
      return;
    }
    const raw = options.file as File & { originFileObj?: File };
    const fileToUpload = raw.originFileObj ?? raw;
    const startedAt = Date.now();
    setUploading(true);
    setUploadProgress({
      filename: fileToUpload.name,
      loaded: 0,
      total: fileToUpload.size || 0,
      percent: 0,
      startedAt,
    });
    try {
      await uploadFaissIndex(
        workingOrganizationId,
        fileToUpload,
        ({ loaded, total, percent }) => {
          setUploadProgress({
            filename: fileToUpload.name,
            loaded,
            total: total || fileToUpload.size || 0,
            percent,
            startedAt,
          });
        }
      );
      setUploadProgress((prev) =>
        prev
          ? {
              ...prev,
              loaded: prev.total || prev.loaded,
              percent: 100,
            }
          : prev
      );
      message.success(t("FaissIndexes-UploadSuccess"));
      options.onSuccess();
      setFileList([]);
      setPage(1);
      await fetchIndexes();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("FaissIndexes-UploadError")));
      options.onError(e);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleSetActive = async (fileId: string) => {
    if (!workingOrganizationId) return;
    try {
      await setActiveFaissIndex(workingOrganizationId, fileId);
      message.success(t("FaissIndexes-SetActiveSuccess"));
      await fetchIndexes();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    }
  };

  const handleDeactivate = async () => {
    if (!workingOrganizationId) return;
    try {
      await setActiveFaissIndex(workingOrganizationId, null);
      message.success(t("FaissIndexes-DeactivateSuccess"));
      await fetchIndexes();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await deleteFaissIndex(fileId);
      message.success(t("FaissIndexes-DeleteSuccess"));
      await fetchIndexes();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    }
  };

  const handleDownload = async (record: FaissIndexFile) => {
    setDownloadingId(record.fileId);
    try {
      await downloadFaissIndex(record.fileId, record.originalFilename);
    } catch (e) {
      message.error(getApiErrorMessage(e, t("FaissIndexes-DownloadError")));
    } finally {
      setDownloadingId(null);
    }
  };

  const formatSize = formatBytes;

  const renderActions = (record: FaissIndexFile, compact: boolean) => (
    <div className={styles.actions}>
      <Tooltip title={t("Common-Download")}>
        <Button
          type={compact ? "default" : "link"}
          size={compact ? "small" : "middle"}
          icon={<DownloadOutlined />}
          loading={downloadingId === record.fileId}
          onClick={() => handleDownload(record)}
        >
          {compact ? null : t("Common-Download")}
        </Button>
      </Tooltip>
      {record.active ? (
        <Tooltip title={t("FaissIndexes-Deactivate")}>
          <Button
            type={compact ? "default" : "link"}
            size={compact ? "small" : "middle"}
            icon={<CloseOutlined />}
            onClick={handleDeactivate}
          >
            {compact ? null : t("FaissIndexes-Deactivate")}
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title={t("FaissIndexes-SetActive")}>
          <Button
            type={compact ? "default" : "link"}
            size={compact ? "small" : "middle"}
            icon={<CheckOutlined />}
            onClick={() => handleSetActive(record.fileId)}
          >
            {compact ? null : t("FaissIndexes-SetActive")}
          </Button>
        </Tooltip>
      )}
      <Popconfirm
        title={t("FaissIndexes-DeleteConfirm")}
        onConfirm={() => handleDelete(record.fileId)}
        okText={t("Common-Yes")}
        cancelText={t("Common-No")}
      >
        <Tooltip title={t("Common-Delete")}>
          <Button
            type={compact ? "default" : "link"}
            danger
            size={compact ? "small" : "middle"}
            icon={<DeleteOutlined />}
          >
            {compact ? null : t("Common-Delete")}
          </Button>
        </Tooltip>
      </Popconfirm>
    </div>
  );

  const columns = [
    {
      title: t("FaissIndexes-Filename"),
      dataIndex: "originalFilename",
      key: "originalFilename",
      ellipsis: true,
    },
    {
      title: t("FaissIndexes-Size"),
      dataIndex: "size",
      key: "size",
      width: 100,
      render: (size: number) => formatSize(size),
      responsive: ["lg" as const],
    },
    {
      title: t("FaissIndexes-UploadedAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      ellipsis: true,
      render: (value?: string) =>
        value ? new Date(value).toLocaleString() : "-",
      responsive: ["xl" as const],
    },
    {
      title: t("FaissIndexes-Status"),
      dataIndex: "active",
      key: "active",
      width: 100,
      render: (active: boolean) =>
        active ? (
          <Tag color="green">{t("FaissIndexes-Active")}</Tag>
        ) : (
          <Tag>{t("FaissIndexes-Inactive")}</Tag>
        ),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 128,
      render: (_: unknown, record: FaissIndexFile) => renderActions(record, true),
    },
  ];

  const pagination = getTablePagination(t, {
    current: page,
    total: totalSize,
    onChange: (nextPage) => setPage(nextPage),
  });

  const uploadOverlay = (
    <Modal
      visible={uploading}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={480}
      zIndex={2000}
    >
      <div className={styles.uploadOverlay}>
        <Title level={4} className={styles.uploadOverlayTitle}>
          {t("FaissIndexes-UploadOverlayTitle")}
        </Title>
        <Text type="secondary" className={styles.uploadOverlayHint}>
          {t("FaissIndexes-UploadOverlayHint")}
        </Text>
        {uploadProgress?.filename && (
          <Text strong className={styles.uploadOverlayFile}>
            {uploadProgress.filename}
          </Text>
        )}
        <Progress
          percent={uploadProgress?.percent ?? 0}
          status="active"
          strokeWidth={12}
        />
        <Text type="secondary" className={styles.uploadOverlayMeta}>
          {t("FaissIndexes-UploadProgress", {
            percent: uploadProgress?.percent ?? 0,
            loaded: formatBytes(uploadProgress?.loaded),
            total: formatBytes(uploadProgress?.total),
          })}
        </Text>
        {etaLabel && (
          <Text type="secondary" className={styles.uploadOverlayEta}>
            {etaLabel}
          </Text>
        )}
      </div>
    </Modal>
  );

  const content = (
    <div className={styles.wrapper} style={embedded ? { padding: 0 } : undefined}>
      {uploadOverlay}
      {!embedded && (
        <div className={styles.header}>
          <Title level={3}>{t("FaissIndexes-Title")}</Title>
          <Text type="secondary">{t("FaissIndexes-Description")}</Text>
        </div>
      )}

      {!workingOrganizationId ? (
        <Card className={styles.uploadCard}>
          <Text type="secondary">
            {t("FaissIndexes-SelectOrganizationFirst")}
          </Text>
        </Card>
      ) : (
        <>
          <Card className={styles.uploadCard}>
            <div>
              <Text strong>{t("FaissIndexes-Organization")}: </Text>
              <Text>{workingOrganizationName}</Text>
            </div>
            <div style={{ marginTop: 16 }}>
              <Text strong>{t("FaissIndexes-Upload")}</Text>
              <div style={{ marginTop: 8 }}>
                <ReusableUpload
                  accept=".index,application/octet-stream"
                  loading={uploading}
                  disabled={uploading}
                  fileList={fileList}
                  onChange={(info) => setFileList(info.fileList)}
                  onUploadRequest={handleUploadRequest}
                  mode="full"
                  fullWidth
                  showUploadButton
                />
              </div>
            </div>
          </Card>

          <Card className={styles.settingsCard}>
            <Title level={5} style={{ marginTop: 0 }}>
              {t("FaissIndexes-SettingsTitle")}
            </Title>
            <Text type="secondary">
              {t("FaissIndexes-SettingsDescription", {
                organization: workingOrganizationName || workingOrganizationId,
              })}
            </Text>
            {settingsLoading ? (
              <div className={styles.settingsGrid}>
                {[0, 1, 2, 3, 4, 5].map((key) => (
                  <Skeleton.Input
                    key={key}
                    active
                    style={{ width: "100%", height: 32 }}
                  />
                ))}
              </div>
            ) : (
              <Form
                form={settingsForm}
                layout="vertical"
                initialValues={DEFAULT_SETTINGS}
                className={styles.settingsGrid}
              >
                <Form.Item
                  name="topK"
                  label={t("FaissIndexes-SettingsTopK")}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={500} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="maxDistance"
                  label={t("FaissIndexes-SettingsMaxDistance")}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={1000} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="maxPriceDifferencePerc"
                  label={t("FaissIndexes-SettingsMaxPriceDiff")}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={1000} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="skipDuplicates"
                  label={t("FaissIndexes-SettingsSkipDuplicates")}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="sameCategory"
                  label={t("FaissIndexes-SettingsSameCategory")}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="sameBrand"
                  label={t("FaissIndexes-SettingsSameBrand")}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Form>
            )}
            <div className={styles.settingsActions}>
              <Button
                type="primary"
                loading={settingsSaving}
                disabled={settingsLoading || uploading}
                onClick={handleSaveSettings}
              >
                {t("FaissIndexes-SettingsSave")}
              </Button>
            </div>
          </Card>

          {loading && files.length === 0 ? (
            isMobileList ? (
              <>
                {[0, 1, 2].map((key) => (
                  <Card key={key} className={styles.mobileCard} size="small">
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: "70%", height: 18 }}
                    />
                    <div className={styles.mobileCardMeta}>
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 120, height: 14 }}
                      />
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 160, height: 14 }}
                      />
                      <Skeleton.Button
                        active
                        size="small"
                        style={{ width: 72, height: 22 }}
                      />
                    </div>
                    <div className={styles.mobileCardActions}>
                      <Skeleton.Button active size="small" />
                      <Skeleton.Button active size="small" />
                    </div>
                  </Card>
                ))}
              </>
            ) : (
              <div className={styles.tableWrap}>
                <Table
                  rowKey="key"
                  pagination={false}
                  tableLayout="fixed"
                  columns={columns.map((col) => ({
                    ...col,
                    render: () => (
                      <Skeleton.Input
                        active
                        size="small"
                        style={{
                          width:
                            col.key === "actions"
                              ? 72
                              : col.key === "active"
                              ? 64
                              : "80%",
                          height: 16,
                          minWidth: 40,
                        }}
                      />
                    ),
                  }))}
                  dataSource={[
                    { key: "s1" },
                    { key: "s2" },
                    { key: "s3" },
                    { key: "s4" },
                  ]}
                />
              </div>
            )
          ) : isMobileList ? (
            <List
              dataSource={files}
              pagination={
                {
                  current: page,
                  total: totalSize,
                  pageSize: 10,
                  onChange: (nextPage: number) => setPage(nextPage),
                  showSizeChanger: false,
                } as any
              }
              renderItem={(record) => (
                <Card className={styles.mobileCard} size="small">
                  <Text strong ellipsis>
                    {record.originalFilename}
                  </Text>
                  <div className={styles.mobileCardMeta}>
                    <Text type="secondary">
                      {t("FaissIndexes-Size")}: {formatSize(record.size)}
                    </Text>
                    <Text type="secondary">
                      {t("FaissIndexes-UploadedAt")}:{" "}
                      {record.createdAt
                        ? new Date(record.createdAt).toLocaleString()
                        : "-"}
                    </Text>
                    {record.active ? (
                      <Tag color="green">{t("FaissIndexes-Active")}</Tag>
                    ) : (
                      <Tag>{t("FaissIndexes-Inactive")}</Tag>
                    )}
                  </div>
                  <div className={styles.mobileCardActions}>
                    {renderActions(record, false)}
                  </div>
                </Card>
              )}
            />
          ) : (
            <div className={styles.tableWrap}>
              <Table
                rowKey="fileId"
                loading={loading && files.length > 0}
                columns={columns}
                dataSource={files}
                pagination={pagination}
                tableLayout="fixed"
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return <AdminValidation>{content}</AdminValidation>;
};

export default FaissIndexes;
